import { useId, useRef, type JSX, type ReactNode, type RefObject } from 'react';

import type { OverlayMotion } from './motion';
import { useFrozenGeometry } from './placement';
import { resolveOverlayScope, scopeRulesOf, type OverlayScope, type OverlayTier } from './scope';
import { overlayLayerStyle, overlayPanelStyle, overlayScrimStyle, overlayTitleStyle } from './styles';
import { useOverlayEntry } from './useOverlay';

/**
 * 浮层。
 *
 * 一次渲染就是一层：`open` 为真时登记进堆栈、渲染固定的浮层；为假时不渲染任何东西。
 *
 * 调用方只需关心 `scope`：
 *   - **省略 = `realtime`**（不遮遥测、不锁滚动、指针放行）；
 *   - 只有不可逆、会危及飞行的动作才显式写 `scope="blocking"`（一般通过 `ConfirmDialog`）。
 *
 * 已知约束（刻意不引入 react-dom 的 portal 来绕开）：整层是 `position: fixed`，
 * 因此承载它的位置不能有 `transform` / `filter` / `will-change` 这类会重新定义包含块的祖先，
 * 也不能被 `overflow: hidden` 裁掉。应用根节点（`#root`）满足这个条件。
 * 若将来要挂到别处，应由应用侧提供 Portal —— `@hgc/ui` 只允许依赖 react。
 *
 * 动效（`motion`）是**可选**的、只影响视觉的：位置永远来自冻结的 `placement`。
 * 见 `motion.ts` —— 那里解释了"为什么动画只碰 transform/opacity"是结构约束而不是约定。
 */

export interface OverlayProps {
  open: boolean;
  /** 关闭请求（Esc、点击外部、点击蒙版之外的关闭按钮）。受控：是否真的关由调用方决定 */
  onClose: () => void;
  /** 无障碍名称。给了 `title` 时会用可见标题作为名称 */
  label: string;
  /**
   * 面板的 `aria-describedby` 指向的 id。
   * 确认类浮层用它接住"后果"那段文字：读屏进入浮层时就能念出会发生什么，
   * 而不是只念一句"立即返航？"。
   */
  describedBy?: string | undefined;
  /** 可见标题 */
  title?: string | undefined;
  /** 默认 `realtime` */
  scope?: OverlayScope | undefined;
  /**
   * 基座层级档位。默认用所在作用域自己那一档；`confirm` 提到最高（见 `--z-confirm`）。
   *
   * 只有按钮确认气泡该传 `confirm`：它是"你还欠一个回答"，
   * 因此必须能从一个正在提问的功能性弹窗里弹出来而不被那个弹窗的蒙版压住。
   */
  tier?: OverlayTier | undefined;
  /** `alertdialog` 用于"必须立刻处理"的内容（危险动作确认）。默认为 `dialog` */
  role?: 'dialog' | 'alertdialog';
  /**
   * 触发元素：浮层相对它定位（位置在打开那一刻冻结），
   * 飞入/飞出的方向也由它决定。类型放宽到 `Element`：这里只取几何。
   */
  anchor?: Element | null;
  /** 打开时把焦点放到这里；不给则聚焦面板容器本身 */
  initialFocus?: RefObject<HTMLElement | null> | undefined;
  /** 只能收窄作用域规则：`blocking` 永远不允许点击外部关闭 */
  dismissOnOutsidePointer?: boolean | undefined;
  maxWidth?: number | undefined;
  /** 垂直对齐。默认 `top`（沿用冻结位置）；`center` 用于功能性弹窗 */
  verticalAlign?: 'top' | 'center' | undefined;
  /** 阴影策略。按钮确认气泡传 `none`（显式，见 `OverlayPanelStyleOptions`） */
  panelShadow?: 'none' | 'token' | undefined;
  /** 视觉阶段。不传 = 不做动画（即时出现/消失） */
  motion?: OverlayMotion | undefined;
  children: ReactNode;
}

export function Overlay(props: OverlayProps): JSX.Element | null {
  const { open, label, title, onClose, children } = props;
  const scope = resolveOverlayScope(props.scope);
  const rules = scopeRulesOf(scope);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const motion = props.motion;

  /*
   * 收回动画期间这一层在交互意义上**已经关掉了**，只是还在画最后 140ms。
   *
   * 不给它单独一个开关的话，那 140ms 里它会：留在无障碍树里（于是读屏软件能念出
   * 用户刚回答完的问题、按名字查询会命中两个同名按钮）、继续扣押焦点陷阱
   *（Tab 出不去）。三样都是"看起来只是动画"却能被键盘与读屏用户碰到的问题。
   *
   * 所以这里把"是否还活着"（`interactive`）与"是否还画着"（`open`）分开：
   * `useOverlayEntry` 拿到的是前者，出栈与焦点归还因此在用户作出选择的**那一刻**发生；
   * 冻结位置（`useFrozenGeometry`）拿到的仍是后者，因为飞入/飞出的方向必须是同一个快照。
   */
  const exiting = motion === 'exiting';
  const interactive = open && !exiting;

  const entry = useOverlayEntry({
    open: interactive,
    scope,
    label,
    onClose,
    panelRef,
    initialFocus: props.initialFocus,
    trapFocus: rules.trapFocus,
    // 作用域是"允许什么"的上界，调用方只能在允许的范围内再收窄
    dismissOnOutsidePointer: rules.dismissOnOutsidePointer && (props.dismissOnOutsidePointer ?? true),
  });

  // 位置与锚点矩形来自同一次快照：飞入/飞出的方向必须是"打开那一刻"的方向
  const geometry = useFrozenGeometry({
    open,
    anchor: props.anchor ?? null,
    maxWidth: props.maxWidth,
  });

  if (!open) return null;
  const hasTitle = title !== undefined && title !== '';

  return (
    <div
      data-hgc-overlay-layer=""
      data-overlay-scope={scope}
      data-overlay-depth={entry.depth}
      data-overlay-top={entry.isTop ? 'true' : 'false'}
      data-overlay-tier={props.tier ?? 'default'}
      // 与 `interactive` 同一件事的另一个出口：无障碍树。`aria-hidden` 一挂，
      // 按角色查询不再命中它，读屏软件也不会念它 —— 视觉上它还在收，但"问题已经答完"了。
      aria-hidden={exiting ? true : undefined}
      style={overlayLayerStyle(scope, entry.depth, props.tier)}
    >
      {rules.scrim ? <div data-hgc-overlay-scrim="" aria-hidden="true" style={overlayScrimStyle()} /> : null}
      <div
        ref={panelRef}
        role={props.role ?? 'dialog'}
        // 显式写成 "false" 而不是省略：DOM 里看得见"这不是模态"，读屏软件也拿到确定的值
        aria-modal={rules.ariaModal}
        aria-label={hasTitle ? undefined : label}
        aria-labelledby={hasTitle ? titleId : undefined}
        aria-describedby={props.describedBy}
        // -1 让面板本身可编程聚焦：打开时先把焦点收进浮层，键盘才有起点
        tabIndex={-1}
        onKeyDown={entry.onKeyDown}
        data-hgc-overlay-panel=""
        data-overlay-scope={scope}
        data-overlay-motion={motion}
        style={overlayPanelStyle(geometry.placement, {
          shadow: props.panelShadow ?? 'token',
          verticalAlign: props.verticalAlign ?? 'top',
          motion,
          flyOrigin: geometry.anchor,
        })}
      >
        {hasTitle ? (
          <div id={titleId} data-hgc-overlay-title="" style={overlayTitleStyle()}>
            {title}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
}
